/**
 * 売上集計（定義の版つき台帳）を画面の表・チャートの形にする表示の計算。
 *
 * 会計の定義は台帳の側にあり、ここで決めるのは見せ方だけ（小計の足し方・退会失効の出し入れ・
 * チャートの系列のまとめ方・粒度）。チャートの期間ごとの売上高の合計に数える行は、台帳の側の
 * isRevenueSalesAmount（締め・予実・版の比較と同じ判定）で決める。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 台帳の行を勘定＞プラン＞内訳の折りたたみ表と、勘定別の積み上げチャートの形に並べ替え、チャートの期間ごとに売上高の合計を持たせる。
 * @graph-connects none
 */
import {
  isRevenueLedgerBalance,
  isRevenueSalesAmount,
  REVENUE_LEDGER_BREAKDOWN_LABELS,
  REVENUE_LEDGER_BUCKET_LABELS,
  REVENUE_LEDGER_PLAN_LABELS,
  RevenueLedgerBreakdownSchema,
  RevenueLedgerBucketSchema,
  RevenueLedgerPlanSchema,
  type RevenueLedgerBreakdown,
  type RevenueLedgerBucket,
  type RevenueLedgerCell,
  type RevenueLedgerDailyRow,
  type RevenueLedgerRow,
  type RevenueLedgerVersion,
} from '@cortex/domain-ac-mens-hub-api';
import { AXIS_TICK_STYLE } from './chart-theme.js';
import type { Granularity } from './kpi-aggregation.js';
import { formatYen, quarterKey, weekKey, weekLabel, yearKey } from './revenue-aggregation.js';

/**
 * セルに出す金額の種類（税抜・消費税・税込）。
 * @graph-connects none
 */
export type LedgerTaxMode = 'net' | 'tax' | 'incl';

/** 月1マスぶんの金額（v0 は税・税込を持たないので null）。 */
type Amounts = Pick<RevenueLedgerCell, 'amountNet' | 'consumptionTax' | 'amountInclTax'>;

/**
 * 金額の組から、選んだ種類の値を取り出す。
 *
 * @param amounts 税抜・消費税・税込の組
 * @param mode 出す種類
 * @returns 金額（その版が持たない値なら null）
 * @graph-connects none
 */
export function amountOf(amounts: Amounts, mode: LedgerTaxMode): number | null {
  if (mode === 'tax') return amounts.consumptionTax;
  if (mode === 'incl') return amounts.amountInclTax;
  return amounts.amountNet;
}

/**
 * 折りたたみ表の1行（勘定・プラン・内訳のどれか）。
 * @graph-connects none
 */
export interface LedgerTreeNode {
  id: string;
  level: 'bucket' | 'plan' | 'breakdown';
  label: string;
  /** 月（YYYY-MM）ごとの金額。値の無い月は入らない */
  amountsByMonth: Map<string, Amounts>;
  children: LedgerTreeNode[];
  /** 退会失効を合計に含めないとき、その行は残したまま小計から外す */
  excludedFromTotal: boolean;
}

/** 片方でも null（その版が持たない値）なら null。 @graph-connects none */
function addOrNull(a: number | null, b: number | null): number | null {
  return a === null || b === null ? null : a + b;
}

/** @graph-connects none */
function addAmounts(target: Map<string, Amounts>, month: string, a: Amounts): void {
  const current = target.get(month);
  target.set(
    month,
    current === undefined
      ? { amountNet: a.amountNet, consumptionTax: a.consumptionTax, amountInclTax: a.amountInclTax }
      : {
          amountNet: current.amountNet + a.amountNet,
          consumptionTax: addOrNull(current.consumptionTax, a.consumptionTax),
          amountInclTax: addOrNull(current.amountInclTax, a.amountInclTax),
        },
  );
}

/**
 * 小計に入れる行か。貸倒は残高と増減が並ぶため、足してよいのは残高の行だけ。
 * @graph-connects none
 */
function countsTowardSubtotal(
  bucket: RevenueLedgerBucket,
  breakdown: RevenueLedgerBreakdown,
): boolean {
  return bucket !== 'bad_debt' || isRevenueLedgerBalance(bucket, breakdown);
}

/**
 * 正式版の台帳の行を、勘定＞プラン＞内訳の折りたたみ表にする。
 * 勘定・プランの行は、その下の内訳の小計（退会失効を含めないときは除いた値）を持つ。
 *
 * @param rows 台帳の月次の行（正式版）
 * @param includeWithdrawal 退会失効を営業外収益の小計に含めるか
 * @returns 勘定の順（売上高・営業外収益・前受金・貸倒）に並んだ表の木
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 台帳の行を勘定＞プラン＞内訳の折りたたみ表にし、小計と退会失効の出し入れを決める。
 * @graph-connects none
 */
export function buildLedgerTree(
  rows: readonly RevenueLedgerRow[],
  includeWithdrawal: boolean,
): LedgerTreeNode[] {
  const bucketOrder: readonly string[] = RevenueLedgerBucketSchema.options;
  const planOrder: readonly string[] = RevenueLedgerPlanSchema.options;
  const breakdownOrder: readonly string[] = RevenueLedgerBreakdownSchema.options;
  const parsed = rows.flatMap((row) => {
    const bucket = RevenueLedgerBucketSchema.safeParse(row.bucket);
    const breakdown = RevenueLedgerBreakdownSchema.safeParse(row.breakdown);
    // v0 の行（勘定 v0・旧列名）はこの表の対象外
    return bucket.success && breakdown.success
      ? [{ row, bucket: bucket.data, breakdown: breakdown.data }]
      : [];
  });
  parsed.sort(
    (a, b) =>
      bucketOrder.indexOf(a.bucket) - bucketOrder.indexOf(b.bucket) ||
      planOrder.indexOf(a.row.planCode) - planOrder.indexOf(b.row.planCode) ||
      breakdownOrder.indexOf(a.breakdown) - breakdownOrder.indexOf(b.breakdown),
  );

  const buckets = new Map<RevenueLedgerBucket, LedgerTreeNode>();
  const plans = new Map<string, LedgerTreeNode>();
  for (const { row, bucket, breakdown } of parsed) {
    const bucketNode = buckets.get(bucket) ?? {
      id: bucket,
      level: 'bucket' as const,
      label: REVENUE_LEDGER_BUCKET_LABELS[bucket],
      amountsByMonth: new Map<string, Amounts>(),
      children: [],
      excludedFromTotal: false,
    };
    buckets.set(bucket, bucketNode);
    const planId = `${bucket}/${row.planCode}`;
    let planNode = plans.get(planId);
    if (planNode === undefined) {
      planNode = {
        id: planId,
        level: 'plan',
        label: REVENUE_LEDGER_PLAN_LABELS[row.planCode],
        amountsByMonth: new Map<string, Amounts>(),
        children: [],
        excludedFromTotal: false,
      };
      plans.set(planId, planNode);
      bucketNode.children.push(planNode);
    }
    const excluded =
      !includeWithdrawal && bucket === 'non_operating_income' && breakdown === 'expiry_withdrawal';
    const leaf: LedgerTreeNode = {
      id: `${planId}/${breakdown}`,
      level: 'breakdown',
      label: REVENUE_LEDGER_BREAKDOWN_LABELS[breakdown],
      amountsByMonth: new Map(),
      children: [],
      excludedFromTotal: excluded,
    };
    const counts = !excluded && countsTowardSubtotal(bucket, breakdown);
    for (const cell of row.cells) {
      addAmounts(leaf.amountsByMonth, cell.month, cell);
      if (counts) {
        addAmounts(planNode.amountsByMonth, cell.month, cell);
        addAmounts(bucketNode.amountsByMonth, cell.month, cell);
      }
    }
    planNode.children.push(leaf);
  }
  return [...buckets.values()];
}

/**
 * チャートの系列。売上高は勘定の中身ごとに積み上げ、取消はマイナス側に積む。
 * 営業外収益は売上高とは別の系列にして、凡例で表示・非表示を切り替える。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上高を勘定別に積み上げる系列と、営業外収益を別に出す系列の定義。
 * @graph-connects none
 */
export const LEDGER_CHART_SERIES = [
  {
    key: 'membershipMonthly',
    labelKey: 'revenue.ledger.seriesMembershipMonthly',
    bucket: 'sales',
    breakdowns: ['membership_ticket', 'purchased_ticket'],
    stack: 'sales',
  },
  {
    key: 'membershipBimonthly',
    labelKey: 'revenue.ledger.seriesMembershipBimonthly',
    bucket: 'sales',
    breakdowns: ['membership_fee'],
    stack: 'sales',
  },
  {
    key: 'penalty',
    labelKey: 'revenue.ledger.seriesPenalty',
    bucket: 'sales',
    breakdowns: ['penalty'],
    stack: 'sales',
  },
  {
    key: 'buyout',
    labelKey: 'revenue.ledger.seriesBuyout',
    bucket: 'sales',
    breakdowns: ['buyout'],
    stack: 'sales',
  },
  {
    key: 'cancel',
    labelKey: 'revenue.ledger.seriesCancel',
    bucket: 'sales',
    breakdowns: ['cancel_membership_fee', 'cancel_penalty', 'cancel_buyout'],
    stack: 'sales',
  },
  {
    key: 'nonOperating',
    labelKey: 'revenue.ledger.seriesNonOperating',
    bucket: 'non_operating_income',
    breakdowns: ['expiry_due', 'expiry_withdrawal'],
    stack: 'nonOperating',
  },
] as const satisfies readonly {
  key: string;
  labelKey: string;
  bucket: RevenueLedgerBucket;
  breakdowns: readonly RevenueLedgerBreakdown[];
  stack: string;
}[];

/** チャート系列のキー。 */
export type LedgerChartSeriesKey = (typeof LEDGER_CHART_SERIES)[number]['key'];

/**
 * チャートの1本（期間のラベルと系列ごとの税抜、その期間の売上高の合計）。
 * salesTotal は締め・予実・版の比較と同じ判定（isRevenueSalesAmount）で数えた売上高の合計（税抜。取消のマイナスを含み、
 * 営業外収益は含めない）。凡例で系列を隠しても変わらない。
 *
 * @graph-business チャートの1期間ぶんの値。勘定別の系列の税抜と、凡例の表示に左右されない売上高の合計（税抜・営業外収益を含めない）を持つ。
 * @graph-connects none
 */
export type LedgerChartRow = { label: string; salesTotal: number } & Record<
  LedgerChartSeriesKey,
  number
>;

/** @graph-connects none */
function emptyChartRow(label: string): LedgerChartRow {
  return {
    label,
    salesTotal: 0,
    membershipMonthly: 0,
    membershipBimonthly: 0,
    penalty: 0,
    buyout: 0,
    cancel: 0,
    nonOperating: 0,
  };
}

/** @graph-connects none */
function seriesOf(
  bucket: string,
  breakdown: string,
  includeWithdrawal: boolean,
): LedgerChartSeriesKey | null {
  if (!includeWithdrawal && breakdown === 'expiry_withdrawal') return null;
  const series = LEDGER_CHART_SERIES.find(
    (s) => s.bucket === bucket && (s.breakdowns as readonly string[]).includes(breakdown),
  );
  return series === undefined ? null : series.key;
}

/**
 * 期間の行に、台帳の1行ぶんの金額を系列と売上高の合計へ足す。
 * 売上高の合計に入れるかは isRevenueSalesAmount で決める（締め・予実・版の比較と同じ判定）。
 * @graph-connects isRevenueSalesAmount [calls] 売上高に入る行の判定
 */
function accumulate(
  rows: Map<string, LedgerChartRow>,
  entry: {
    key: string;
    label: string;
    series: LedgerChartSeriesKey;
    source: { bucket: string; breakdown: string };
    amount: number;
  },
): void {
  const { key, label, series, source, amount } = entry;
  const row = rows.get(key) ?? emptyChartRow(label);
  row[series] += amount;
  if (isRevenueSalesAmount(source.bucket, source.breakdown)) row.salesTotal += amount;
  rows.set(key, row);
}

/**
 * 粒度が日次・週次か。日次・週次は日次の増加（前日からの増加）から作る。
 *
 * @param granularity 粒度
 * @returns 日次・週次なら true
 * @graph-business 売上集計（正式版）のチャートで、日次の増加（前日からの増加）から作る粒度（日次・週次）かを判定する。
 * @graph-connects none
 */
export function isLedgerIncreaseGranularity(granularity: Granularity): boolean {
  return granularity === 'daily' || granularity === 'weekly';
}

/**
 * チャートの行を粒度に合わせて作る。日次・週次は日次の増加から、月次・四半期・年次は月次の台帳から作る。
 * 各行はその期間の売上高の合計（salesTotal）も持つ。
 *
 * @param params.granularity 粒度
 * @param params.monthlyRows 台帳の月次の行
 * @param params.dailyRows 日次の増加
 * @param params.includeWithdrawal 退会失効を営業外収益に含めるか
 * @returns 期間の昇順に並んだチャートの行
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上高を勘定別、営業外収益を別系列にしたチャートの行を、粒度に合わせて作る。各行は期間の売上高の合計（税抜・営業外収益を含めない）を持つ。
 * @graph-connects isLedgerIncreaseGranularity [calls] 日次の増加から作る粒度の判定
 * @graph-connects quarterKey [calls] 四半期のキー
 * @graph-connects weekKey [calls] 週のキー
 * @graph-connects yearKey [calls] 年のキー
 */
export function buildLedgerChartRows(params: {
  granularity: Granularity;
  monthlyRows: readonly RevenueLedgerRow[];
  dailyRows: readonly RevenueLedgerDailyRow[];
  includeWithdrawal: boolean;
}): LedgerChartRow[] {
  const { granularity, monthlyRows, dailyRows, includeWithdrawal } = params;
  const result = new Map<string, LedgerChartRow>();

  if (isLedgerIncreaseGranularity(granularity)) {
    for (const row of dailyRows) {
      const series = seriesOf(row.bucket, row.breakdown, includeWithdrawal);
      if (series === null) continue;
      const key = granularity === 'daily' ? row.date : weekKey(row.date);
      const label = granularity === 'daily' ? row.date : weekLabel(key);
      accumulate(result, { key, label, series, source: row, amount: row.amountNet });
    }
  } else {
    for (const row of monthlyRows) {
      const series = seriesOf(row.bucket, row.breakdown, includeWithdrawal);
      if (series === null) continue;
      for (const cell of row.cells) {
        const date = `${cell.month}-01`;
        const key =
          granularity === 'monthly'
            ? cell.month
            : granularity === 'quarterly'
              ? quarterKey(date)
              : yearKey(date);
        accumulate(result, { key, label: key, series, source: row, amount: cell.amountNet });
      }
    }
  }

  return [...result.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([, row]) => row);
}

/**
 * 棒の上の売上高の合計の数字の文字の大きさ（px）。軸の目盛りと同じ大きさにし、描画と、棒の間に収まるかの見積もりの
 * 両方で使う。
 * @graph-business チャートの棒の上に出す売上高の合計の数字の文字の大きさ。描画と、数字が棒の間に収まるかの見積もりで同じ値を使う。
 * @graph-connects AXIS_TICK_STYLE [references] 軸の目盛りの文字の大きさ
 */
export const LEDGER_TOTAL_LABEL_FONT_SIZE = AXIS_TICK_STYLE.fontSize;

/**
 * 数字1文字の幅の見積もり（文字の大きさに対する比）。数字・¥ は 0.6 前後、桁区切りはそれより狭いので、
 * 広めに見積もって重なる側に倒さない。
 * @graph-connects none
 */
const TOTAL_LABEL_CHAR_WIDTH_RATIO = 0.62;

/**
 * 数字を縦書きにしたときの1行の太さの見積もり（文字の大きさに対する比）。
 * @graph-connects none
 */
const TOTAL_LABEL_LINE_HEIGHT_RATIO = 1.2;

/**
 * 隣の期間の数字との間にあける余白（px）。
 * @graph-connects none
 */
const TOTAL_LABEL_GAP = 6;

/**
 * 数字を出さないときのグラフの上の余白（px）。
 * @graph-connects none
 */
const CHART_MARGIN_TOP_PLAIN = 4;

/**
 * 横書きの数字を出すときのグラフの上の余白（px）。いちばん高い棒の上の数字が切れないようにする。
 * @graph-connects none
 */
const CHART_MARGIN_TOP_HORIZONTAL = 20;

/**
 * 縦書きの数字の長さの見積もりに足す、グラフの上の余白（px）。
 * @graph-connects none
 */
const CHART_MARGIN_TOP_VERTICAL_EXTRA = 8;

/**
 * 棒の上の売上高の合計の数字の置き方と、そのためにグラフの上にあける余白（px）。
 * @graph-business チャートの棒の上の売上高の合計を横書き・縦書き・出さないのどれで置くかと、そのためのグラフの上の余白。
 * @graph-connects none
 */
export interface LedgerTotalLabelLayout {
  placement: 'horizontal' | 'vertical' | 'none';
  marginTop: number;
}

/**
 * 棒の上に売上高の合計の数字をどう置くかを決める。日次・週次は期間が日・週の単位で棒が多くなるので出さない
 * （この粒度は日次の増加から作る粒度と同じなので isLedgerIncreaseGranularity で判定する）。月次・四半期・年次は、
 * いちばん長い数字が1期間ぶんの幅に収まれば横書き、収まらなければ縦書き、縦書きでも隣と重なるほど棒が多ければ
 * 出さない（吹き出しにはどの場合も出る）。グラフの幅を測れていない間は出さない（重なった数字を出さないため）。
 *
 * @param params.granularity 粒度
 * @param params.plotWidth 棒を描く範囲の幅（px。測れていなければ null）
 * @param params.rows チャートの行
 * @returns 数字の置き方とグラフの上の余白
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business チャートの棒の上に売上高の合計を出す粒度と、棒の多さ（1期間ぶんの幅）に応じた横書き・縦書き・出さないの置き方を決める。
 * @graph-connects isLedgerIncreaseGranularity [calls] 日次・週次の判定
 * @graph-connects formatYen [calls] 金額の書式（数字の長さの見積もり）
 */
export function ledgerTotalLabelLayout(params: {
  granularity: Granularity;
  plotWidth: number | null;
  rows: readonly LedgerChartRow[];
}): LedgerTotalLabelLayout {
  const { granularity, plotWidth, rows } = params;
  if (isLedgerIncreaseGranularity(granularity) || plotWidth === null || rows.length === 0) {
    return { placement: 'none', marginTop: CHART_MARGIN_TOP_PLAIN };
  }
  const periodWidth = plotWidth / rows.length;
  const longest = Math.max(...rows.map((row) => formatYen(row.salesTotal).length));
  const labelLength = longest * LEDGER_TOTAL_LABEL_FONT_SIZE * TOTAL_LABEL_CHAR_WIDTH_RATIO;
  if (labelLength + TOTAL_LABEL_GAP <= periodWidth) {
    return { placement: 'horizontal', marginTop: CHART_MARGIN_TOP_HORIZONTAL };
  }
  if (
    LEDGER_TOTAL_LABEL_FONT_SIZE * TOTAL_LABEL_LINE_HEIGHT_RATIO + TOTAL_LABEL_GAP <=
    periodWidth
  ) {
    return {
      placement: 'vertical',
      marginTop: Math.ceil(labelLength) + CHART_MARGIN_TOP_VERTICAL_EXTRA,
    };
  }
  return { placement: 'none', marginTop: CHART_MARGIN_TOP_PLAIN };
}

/**
 * 今日（JST）から日数をずらした日の YYYY-MM-DD。
 *
 * @param offsetDays ずらす日数（前日なら -1）
 * @param now 基準の時刻（既定は現在）
 * @returns YYYY-MM-DD
 * @graph-connects none
 */
export function jstDate(offsetDays: number, now: number = Date.now()): string {
  const d = new Date(now + 9 * 60 * 60 * 1000);
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

/**
 * 開始月から終了月までの月（YYYY-MM）。版の比較の月の選択肢に使う。
 *
 * @param fromMonth 開始月
 * @param toMonth 終了月
 * @returns 両端を含む月の昇順
 * @graph-connects none
 */
export function monthRange(fromMonth: string, toMonth: string): string[] {
  const months: string[] = [];
  const cursor = new Date(`${fromMonth}-01T00:00:00Z`);
  while (cursor.toISOString().slice(0, 7) <= toMonth) {
    months.push(cursor.toISOString().slice(0, 7));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return months;
}

/**
 * 版の選択肢（画面の並び順）。labelKey は描画側で t() する翻訳キー。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上ページと版の比較で出す定義の版の選択肢と表示名を1か所で持つ。
 * @graph-connects none
 */
export const REVENUE_VERSION_OPTIONS = [
  { version: 'v0.1', labelKey: 'revenue.versionV01' },
  { version: 'v0', labelKey: 'revenue.versionV0' },
] as const satisfies readonly { version: RevenueLedgerVersion; labelKey: string }[];

/** 版から表示名の翻訳キーを引く表（REVENUE_VERSION_OPTIONS から導出）。 @graph-connects none */
const LABEL_KEY_BY_VERSION = Object.fromEntries(
  REVENUE_VERSION_OPTIONS.map((o) => [o.version, o.labelKey]),
) as Record<RevenueLedgerVersion, string>;

/**
 * 版の表示名の翻訳キー。
 *
 * @param version 定義の版
 * @returns 翻訳キー
 * @graph-connects REVENUE_VERSION_OPTIONS [references] 版の選択肢
 */
export function versionLabelKey(version: RevenueLedgerVersion): string {
  return LABEL_KEY_BY_VERSION[version];
}
